import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import productData from "../Datas/productData";
import "../css/header.scss";

function Header() {
  const { cartItems, wishlistItems } = useContext(CartContext);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isShopMenuOpen, setIsShopMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const cartCount = cartItems.reduce(
    (total, item) => total + (Number(item.quantity) || 0),
    0
  );
  const wishlistCount = wishlistItems.length;
  const isOverlayOpen = isSearchOpen || isMenuOpen || isShopMenuOpen;

  useEffect(() => {
    setIsSearchOpen(false);
    setIsMenuOpen(false);
    setIsShopMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const pageTitles = {
      "/": siteConfig.tagline,
      "/shop": "Shop All",
      "/cartpage": "Your Cart",
      "/checkout": "Checkout",
      "/paysuccess": "Order Confirmation",
      "/about": "About",
      "/contact": "Contact",
      "/shipping": "Shipping",
      "/returns": "Returns & Exchanges",
      "/privacy": "Privacy",
      "/terms": "Terms",
    };
    const productMatch = location.pathname.match(/^\/shopDetails\/(\d+)$/);
    const productTitle = productMatch
      ? productData.find((product) => product.id === Number(productMatch[1]))?.name
      : "";
    const pageTitle = productTitle || pageTitles[location.pathname];
    document.title = pageTitle
      ? `${pageTitle} | ${siteConfig.brandName}`
      : siteConfig.brandName;

    const description = document.querySelector('meta[name="description"]');
    description?.setAttribute("content", siteConfig.description);
  }, [location.pathname]);

  useEffect(() => {
    if (!isSearchOpen) return undefined;

    const focusTimer = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [isSearchOpen]);

  useEffect(() => {
    if (!isOverlayOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    const focusTimer = window.setTimeout(() => {
      const overlay = document.querySelector(
        ".site-header__search, .site-header__mobile-nav, .site-header__mega"
      );
      overlay?.querySelector("input, button, a")?.focus();
    }, 0);
    const handleOverlayKeys = (event) => {
      if (event.key === "Escape") {
        setIsSearchOpen(false);
        setIsMenuOpen(false);
        setIsShopMenuOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const overlay = document.querySelector(
        ".site-header__search, .site-header__mobile-nav, .site-header__mega"
      );
      const focusable = overlay?.querySelectorAll("input, button, a");
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleOverlayKeys);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleOverlayKeys);
      previousFocus?.focus?.();
    };
  }, [isOverlayOpen]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openSearch = () => {
    setIsMenuOpen(false);
    setIsShopMenuOpen(false);
    setIsSearchOpen(true);
  };

  const toggleMenu = () => {
    setIsSearchOpen(false);
    setIsShopMenuOpen(false);
    setIsMenuOpen((isOpen) => !isOpen);
  };

  const closeOverlays = () => {
    setIsSearchOpen(false);
    setIsMenuOpen(false);
    setIsShopMenuOpen(false);
  };

  const handleNavigation = () => {
    closeOverlays();
    scrollToTop();
  };

  const handleSearch = (event) => {
    event.preventDefault();
    navigate(`/shop?search=${encodeURIComponent(searchTerm.trim())}`);
    closeOverlays();
    scrollToTop();
  };

  return (
    <header className="site-header">
      <div className="site-header__field-strip" aria-hidden="true">
        <div className="site-header__field-track">
          {[...siteConfig.fieldStrip, ...siteConfig.fieldStrip].map(
            (item, index) => (
              <span key={`${item}-${index}`}>
                {item} <i>◆</i>
              </span>
            )
          )}
        </div>
      </div>

      <div className="site-header__announcement">
        <Link to={siteConfig.announcement.to} onClick={handleNavigation}>
          {siteConfig.announcement.text}
        </Link>
      </div>

      <div className="site-header__navigation">
        <div className="site-header__navigation-inner">
          <button
            className={`site-header__menu-toggle${
              isMenuOpen ? " is-active" : ""
            }`}
            type="button"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            onClick={toggleMenu}
          >
            <span />
            <span />
          </button>

          <Link
            className="site-header__wordmark"
            to="/"
            aria-label={siteConfig.logo.alt}
            onClick={handleNavigation}
          >
            {siteConfig.logo.text}
          </Link>

          <nav className="site-header__desktop-nav" aria-label="Main navigation">
            {siteConfig.primaryNavigation.map((item) =>
              item.label === "SHOP NOW" ? (
                <button
                  key={item.label}
                  className="site-header__nav-button"
                  type="button"
                  aria-expanded={isShopMenuOpen}
                  aria-controls="shop-mega-menu"
                  onClick={() => {
                    setIsSearchOpen(false);
                    setIsShopMenuOpen((isOpen) => !isOpen);
                  }}
                >
                  {item.label}
                </button>
              ) : (
                <Link key={item.label} to={item.to} onClick={handleNavigation}>
                  {item.label}
                </Link>
              )
            )}
          </nav>

          <div className="site-header__actions">
            <span className="site-header__currency">
              {siteConfig.currency.code}
            </span>
            <button
              className="site-header__action site-header__search-trigger"
              type="button"
              aria-expanded={isSearchOpen}
              aria-controls="site-search"
              onClick={openSearch}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="11" cy="11" r="6.5" />
                <path d="m16 16 4 4" />
              </svg>
              <span>Search</span>
            </button>
            <Link
              className="site-header__action site-header__wishlist"
              to="/shop?wishlist=true"
              aria-label={`Wishlist with ${wishlistCount} items`}
              onClick={handleNavigation}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20.8 4.8a5.5 5.5 0 0 0-7.8 0L12 5.9l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.4a5.5 5.5 0 0 0 0-7.8Z" />
              </svg>
              <span className="site-header__action-label">Wish</span>
              <span className="site-header__count">{wishlistCount}</span>
            </Link>
            <Link
              className="site-header__action site-header__cart"
              to="/cartpage"
              aria-label={`Cart with ${cartCount} items`}
              onClick={handleNavigation}
            >
              <span className="site-header__action-label">Cart</span>
              <span className="site-header__count">{cartCount}</span>
            </Link>
          </div>
        </div>
      </div>

      {isOverlayOpen && (
        <button
          className="site-header__scrim"
          type="button"
          aria-label="Close navigation overlay"
          onClick={closeOverlays}
        />
      )}

      {isShopMenuOpen && (
        <section
          id="shop-mega-menu"
          className="site-header__mega"
          aria-label="Shop collections"
        >
          <div className="site-header__mega-inner">
            <div className="site-header__mega-group">
              <p>Shop</p>
              {siteConfig.footerNavigation[0].links.map((link) => (
                <Link key={link.label} to={link.to} onClick={handleNavigation}>
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="site-header__mega-group">
              <p>Collections</p>
              <Link to="/shop?category=Shorts" onClick={handleNavigation}>
                Shorts season
              </Link>
              <Link to="/shop?category=Outerwear" onClick={handleNavigation}>
                Light layers
              </Link>
              <Link to="/shop?category=Tees" onClick={handleNavigation}>
                Everyday tees
              </Link>
              <Link to="/shop?category=Bags" onClick={handleNavigation}>
                Packable bags
              </Link>
            </div>
            <Link
              className="site-header__mega-story"
              to="/about"
              onClick={handleNavigation}
            >
              <img src="/img/story-forest.webp" alt="" />
              <span>Made for the long way around ↗</span>
            </Link>
          </div>
        </section>
      )}

      {isSearchOpen && (
        <section
          id="site-search"
          className="site-header__search"
          role="dialog"
          aria-modal="true"
          aria-labelledby="site-search-title"
        >
          <form className="site-header__search-form" onSubmit={handleSearch}>
            <label id="site-search-title" htmlFor="site-search-input">
              Search the collection
            </label>
            <div className="site-header__search-field">
              <input
                ref={searchInputRef}
                id="site-search-input"
                type="search"
                value={searchTerm}
                placeholder="What are you looking for?"
                autoComplete="off"
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <button type="submit">Search</button>
            </div>
          </form>
          <button
            className="site-header__close"
            type="button"
            aria-label="Close search"
            onClick={closeOverlays}
          >
            <span />
            <span />
          </button>
        </section>
      )}

      {isMenuOpen && (
        <nav
          id="mobile-navigation"
          className="site-header__mobile-nav"
          aria-label="Mobile navigation"
        >
          <p>{siteConfig.tagline}</p>
          <div className="site-header__mobile-links">
            {siteConfig.primaryNavigation.map((item) => (
              <Link key={item.label} to={item.to} onClick={handleNavigation}>
                {item.label}
                <span aria-hidden="true">↗</span>
              </Link>
            ))}
          </div>
          <div className="site-header__mobile-utility">
            <button type="button" onClick={openSearch}>
              Search
            </button>
            <Link to="/shop?wishlist=true" onClick={handleNavigation}>
              Wishlist ({wishlistCount})
            </Link>
            <Link to="/contact" onClick={handleNavigation}>
              Contact
            </Link>
            <span>{siteConfig.currency.code}</span>
          </div>
        </nav>
      )}
    </header>
  );
}

export default Header;
